import React, { useEffect, useRef } from 'react';
import { View, StyleSheet, Animated, Easing, Platform } from 'react-native';
import { CORK_BOARD_COLORS } from './corkBoardStyles';

const SKELETON_COUNT = 4;

const SkeletonBar: React.FC<{
  width: number | string;
  height: number;
  opacity: Animated.AnimatedInterpolation<number>;
  borderRadius?: number;
  style?: any;
}> = ({ width, height, opacity, borderRadius = 4, style }) => (
  <Animated.View
    style={[
      styles.bar,
      { width, height, borderRadius, opacity },
      style,
    ]}
  />
);

const SingleCardSkeleton: React.FC<{
  opacity: Animated.AnimatedInterpolation<number>;
}> = ({ opacity }) => (
  <View style={styles.card}>
    {/* Tags row */}
    <View style={styles.tagsRow}>
      <SkeletonBar width={60} height={20} opacity={opacity} borderRadius={10} />
      <SkeletonBar width={72} height={20} opacity={opacity} borderRadius={10} />
    </View>
    {/* Title */}
    <SkeletonBar width="80%" height={16} opacity={opacity} />
    {/* Body lines */}
    <SkeletonBar width="100%" height={12} opacity={opacity} />
    <SkeletonBar width="65%" height={12} opacity={opacity} />
    {/* Footer */}
    <View style={styles.footer}>
      <View style={styles.authorSection}>
        <Animated.View style={[styles.avatarSkeleton, { opacity }]} />
        <SkeletonBar width={80} height={12} opacity={opacity} />
        <SkeletonBar width={50} height={12} opacity={opacity} />
      </View>
      <View style={styles.statsRow}>
        <SkeletonBar width={30} height={12} opacity={opacity} />
        <SkeletonBar width={30} height={12} opacity={opacity} />
      </View>
    </View>
  </View>
);

export const ThreadCardSkeleton: React.FC = () => {
  const shimmerAnim = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.timing(shimmerAnim, {
          toValue: 1,
          duration: 1000,
          easing: Easing.ease,
          useNativeDriver: Platform.OS !== 'web',
        }),
        Animated.timing(shimmerAnim, {
          toValue: 0,
          duration: 1000,
          easing: Easing.ease,
          useNativeDriver: Platform.OS !== 'web',
        }),
      ]),
    );
    animation.start();
    return () => animation.stop();
  }, [shimmerAnim]);

  const opacity = shimmerAnim.interpolate({
    inputRange: [0, 1],
    outputRange: [0.3, 0.7],
  });

  return (
    <View style={styles.container}>
      {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
        <SingleCardSkeleton key={i} opacity={opacity} />
      ))}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    gap: 12,
    paddingHorizontal: 16,
  },
  card: {
    backgroundColor: CORK_BOARD_COLORS.cardBackground,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: CORK_BOARD_COLORS.cardBorder,
    padding: 16,
    gap: 10,
  },
  bar: {
    backgroundColor: CORK_BOARD_COLORS.skeletonBase,
  },
  tagsRow: {
    flexDirection: 'row',
    gap: 6,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  authorSection: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  avatarSkeleton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: CORK_BOARD_COLORS.skeletonBase,
  },
  statsRow: {
    flexDirection: 'row',
    gap: 10,
  },
});
